import { useState } from "react";
import {
  IconShare,
  IconEdit,
  IconX,
  IconCircleCheck,
  IconPlus,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconBrandX,
  IconBrandInstagram,
} from "@tabler/icons-react";
import styles from "./socialLinksCard.module.css";
import Input from "../../FormInput/input";
import Button from "../../Button/button";
import { errorHandler } from "../../../../utils";
import { companyAdditionalInfo } from "../../../../services/clientCalls";

const PLATFORMS = [
  {
    key: "facebookUrl",
    label: "Facebook",
    Icon: IconBrandFacebook,
    iconClass: styles.socialIconFacebook,
    domains: ["facebook.com"],
    placeholder: "https://facebook.com/yourcompany",
    example: "facebook.com/yourpage",
  },
  {
    key: "linkedInUrl",
    label: "LinkedIn",
    Icon: IconBrandLinkedin,
    iconClass: styles.socialIconLinkedin,
    domains: ["linkedin.com"],
    placeholder: "https://linkedin.com/company/yourcompany",
    example: "linkedin.com/company/yourname",
  },
  {
    key: "twitterUrl",
    label: "Twitter / X",
    Icon: IconBrandX,
    iconClass: styles.socialIconTwitter,
    domains: ["x.com", "twitter.com"],
    placeholder: "https://x.com/yourhandle",
    example: "x.com/yourhandle",
  },
  {
    key: "instagramUrl",
    label: "Instagram",
    Icon: IconBrandInstagram,
    iconClass: styles.socialIconInstagram,
    domains: ["instagram.com"],
    placeholder: "https://instagram.com/yourcompany",
    example: "instagram.com/yourcompany",
  },
];

// ── Validate a social URL — domain must match AND have a real path ─────────
function isValidSocialUrl(url, domains) {
  const cleaned = url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "");
  return domains.some((domain) => {
    if (!cleaned.startsWith(domain)) return false;
    const path = cleaned.slice(domain.length);
    return /^\/\S+/.test(path); // must have /something after the domain
  });
}

const SocialLinksCard = ({
  socialLinks = {},
  profileData,
  getCompanyProfile,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [shakeKey, setShakeKey] = useState(null);
  const [load, setLoad] = useState(false);

  const handleEdit = () => {
    if (editing) {
      setEditing(false);
      setDraft({});
      setErrors({});
    } else {
      const seeded = {};
      PLATFORMS.forEach(({ key }) => {
        seeded[key] = profileData[key] || "";
      });
      setDraft(seeded);
      setEditing(true);
      setErrors({});
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const newErrors = {};
    let firstInvalidKey = null;

    PLATFORMS?.forEach(({ key, domains, label, example }) => {
      const val = draft[key]?.trim();
      if (val && !isValidSocialUrl(val, domains)) {
        newErrors[key] =
          `This does not appear to be a valid ${label} profile URL. Please use the format: ${example}`;
        if (!firstInvalidKey) firstInvalidKey = key;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShakeKey(firstInvalidKey);
      setTimeout(() => setShakeKey(null), 600);
      return;
    }

    setErrors({});
    handleSubmit();
    setEditing(false);
    setDraft({});
  };

  const handleSubmit = async (e) => {
    try {
      setLoad(true);
      const res = await companyAdditionalInfo({ ...draft });
      if (res.data) {
        getCompanyProfile();
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };

  return (
    <div className={styles.card} id="socialCard">
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconShare size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>Social Links</div>
            <div className={styles.cardSubtitle}>
              Connect your social profiles
            </div>
          </div>
        </div>

        <div
          className={`${styles.btnGroup} ${editing ? styles.btnGroupEditing : ""}`}
        >
          {editing && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconCircleCheck size={14} />}
              onClick={handleUpdate}
              loading={load}
              disabled={load}
            >
              Update
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            leftIcon={editing ? <IconX size={14} /> : <IconEdit size={14} />}
            onClick={handleEdit}
          >
            {editing ? "Cancel" : "Edit"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        <div className={styles.socialList}>
          {PLATFORMS.map(({ key, label, Icon, iconClass, placeholder }) => {
            const savedUrl = profileData[key] || "";
            const hasUrl = !!savedUrl;

            return (
              <div
                key={key}
                className={`${styles.socialItem} ${editing ? styles.socialItemEditing : ""}`}
              >
                <div className={`${styles.socialIcon} ${iconClass}`}>
                  <Icon size={17} />
                </div>

                <div className={styles.socialInfo}>
                  <div className={styles.socialName}>{label}</div>

                  {/* Display mode */}
                  {!editing &&
                    (hasUrl ? (
                      <div className={styles.socialUrl}>
                        <a
                          href={
                            savedUrl.startsWith("http")
                              ? savedUrl
                              : `https://${savedUrl}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {savedUrl}
                        </a>
                      </div>
                    ) : (
                      <div
                        className={`${styles.socialUrl} ${styles.socialUrlEmpty}`}
                      >
                        Not connected
                      </div>
                    ))}

                  {/* Edit mode */}
                  {editing && (
                    <Input
                      type="text"
                      size="sm"
                      placeholder={placeholder}
                      value={draft[key] ?? ""}
                      shake={shakeKey === key}
                      error={errors[key]}
                      onChange={(e) => {
                        setDraft((d) => ({ ...d, [key]: e.target.value }));
                        setErrors((er) => ({ ...er, [key]: "" }));
                      }}
                    />
                  )}
                </div>

                {!editing && !hasUrl && (
                  <IconPlus
                    size={16}
                    style={{ color: "var(--text-muted)", flexShrink: 0 }}
                    onClick={handleEdit}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SocialLinksCard;
